/**
 * The Overview tab — §4 of the record page plan.
 *
 * Four cards, in the order somebody reads them: what is next, what work is
 * open, what is coming, what was said. Each one is a *peek*, three rows deep,
 * with the real list one tap away — because an overview that shows everything
 * is a list, and there is already a list.
 *
 * Next up is the only card that renders when empty. The others appear when
 * they have something to say: a contact with no invoices should not be told
 * so in a card of its own.
 */
import { useState, type ReactNode } from 'react'
import { ACTIVITY_KIND_LABELS } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { NoteBody } from '../components/note-editor.js'
import {
  Avatar, Checkbox, EmptyState, StatusChip, humanize,
} from '../components/primitives.js'
import { VISIT_STATUS_AS_JOB } from '../components/record-list.js'
import { jobMark, visitMark } from '../components/landing-marks.js'
import { RecordCard } from './record-rail.js'
import { AppointmentRow, NoSiteVisitRow } from './appointment-row.js'
import {
  IconCall, IconChevronRight, IconEmail, IconMeeting, IconNote, IconPlus, IconSms, IconTask,
} from '../frame/icons.js'
import { formatRelative } from '../lib/format.js'
import { completeKey, type JobPeek, type RecordActivity, type VisitPeek } from '../lib/use-record.js'

/**
 * What is open on this record, soonest first.
 *
 * The tick box is the point of the card: completing a task here completes it
 * everywhere it appears, because it is one row — the same task on the job,
 * the property and the assignee's own day.
 */
export function NextUpCard({
  items, onComplete, onAdd, onOpenAll, busy, requestWithoutVisit,
}: {
  items: RecordActivity[]
  onComplete: (id: string) => void
  onAdd: () => void
  onOpenAll: () => void
  busy?: string | null
  /**
   * A Requested job New request made with no site visit (Board 9 E): the
   * dashed *No site visit yet* row sits at the top instead of *Nothing
   * scheduled.* (`new-request.md` §15.1).
   */
  requestWithoutVisit?: { jobId: string } | null
}) {
  /*
   * `?focus=next-up&item=<id>` — *Create, book later* lands here on the
   * appointment it made (`new-request.md` §11.4, §15.1): the card rings and
   * so does that row, for the same 1.2 s.
   */
  const [ringing, setRinging] = useState<string | null>(null)
  /*
   * Board 9 F rings the whole card in the accent with a 4px halo when a
   * request lands on it — heavier than `RecordCard`'s own 2px focus ring, which
   * every other card keeps, so it is this card's and for the same 1.2 s.
   */
  const [landed, setLanded] = useState(false)
  return (
    <RecordCard
      title="Next up"
      focusKey="next-up"
      {...(landed ? { className: '!border-accent shadow-[0_0_0_4px_rgb(var(--color-accent-ring))]' } : {})}
      onFocus={() => {
        const item = new URLSearchParams(window.location.search).get('item')
        if (!item) return
        setRinging(item)
        setLanded(true)
        window.setTimeout(() => { setRinging(null); setLanded(false) }, 1200)
      }}
      action={{ label: 'Add', icon: <IconPlus size={13} />, onClick: onAdd }}
      /*
       * The bar is there whether or not there are rows. A card that grows a
       * footer only once it has something in it is a card whose height jumps
       * as data arrives, and on a page of cards that all end the same way the
       * one that sometimes does not is the one that looks broken.
       */
      footer={(
        <button
          type="button"
          onClick={onOpenAll}
          className="-my-1 flex w-full items-center gap-1 py-1 text-[12.5px]
                     font-semibold text-accent"
        >
          See all in Activity
          <IconChevronRight size={13} />
        </button>
      )}
    >
      {requestWithoutVisit && <NoSiteVisitRow jobId={requestWithoutVisit.jobId} />}
      {items.length === 0
        ? (!requestWithoutVisit && (
          <p className="m-0 px-[18px] py-4 text-body text-text-muted">
            Nothing scheduled.
          </p>
        ))
        : items.map((item, index) => (
          <div key={completeKey(item)} data-activity={item.id}
            className={cn('transition-shadow', ringing === item.id
              && 'rounded-[10px] shadow-[inset_0_0_0_2px_rgb(var(--color-accent-line))]',
            /* The card's header already rules under *Next up*: a site visit
               first in the list draws no second rule of its own (Board 9 F). */
            index === 0 && !requestWithoutVisit && item.appointment && '[&>*]:border-t-0')}>
            <ActivityRow
              item={item}
              busy={busy === completeKey(item)}
              /* A repeating meeting's row is its next occurrence: the tick names that one (0152). */
              onComplete={() => onComplete(completeKey(item))}
            />
          </div>
        ))}
    </RecordCard>
  )
}

/**
 * One open activity: tick box, a tile in the kind's colour, what and when.
 *
 * The date is the only thing that changes colour — red, and the words "Overdue
 * 2d", when it has passed. A row that shouted in five places would tell you
 * nothing about which one to do first.
 */
export function ActivityRow({
  item, onComplete, busy,
}: {
  item: RecordActivity
  onComplete: () => void
  busy?: boolean
}) {
  /* A site visit draws as itself — a date tile or a dashed *not booked yet* (§15.1). */
  if (item.appointment) return <AppointmentRow item={item} />
  const overdue = item.dueAt !== null && Date.parse(item.dueAt) < Date.now()

  return (
    <div className="flex items-center gap-3 border-t border-line-soft px-[18px] py-2.5">
      <Checkbox
        checked={Boolean(item.doneAt)}
        disabled={busy}
        onChange={onComplete}
        label={`Complete ${item.title || ACTIVITY_KIND_LABELS[item.kind]}`}
        hideLabel
      />
      <KindTile kind={item.kind} />

      <div className="min-w-0 flex-1">
        <b className={cn('block truncate text-body font-semibold',
          item.doneAt ? 'text-text-faint line-through' : 'text-text')}>
          {item.title || ACTIVITY_KIND_LABELS[item.kind]}
        </b>
        {item.body && (
          <span className="block truncate text-chip text-text-muted">{textOf(item.body)}</span>
        )}
      </div>

      {item.dueAt && (
        <span className={cn('shrink-0 whitespace-nowrap text-[12px] font-semibold',
          overdue ? 'text-danger' : 'text-text-muted')}>
          {overdue ? `Overdue ${overdueDays(item.dueAt)}` : formatRelative(item.dueAt)}
        </span>
      )}
      {item.assigneeName && <Avatar name={item.assigneeName} size={24} />}
    </div>
  )
}

/**
 * The words out of a note's markup, for the places that have one line.
 *
 * A row 42px tall cannot render rich text and cannot print its tags either —
 * "Gate code is <b>4417</b>" is not a summary of anything. The tags come out
 * and the words stay.
 */
const textOf = (html: string): string =>
  html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()

const overdueDays = (dueAt: string): string => {
  const days = Math.floor((Date.now() - Date.parse(dueAt)) / 86_400_000)
  return days >= 1 ? `${days}d` : 'today'
}

/**
 * The 30px rounded tile in the kind's own colour.
 *
 * Its colours come from the `activity` semantic group in `tokens.json`, the
 * same way a status chip's do — §3.1 allows no hand-typed colour in a
 * component, and a kind is exactly the sort of thing that would otherwise
 * collect six of them.
 */
export function KindTile({ kind, size = 30 }: { kind: string; size?: number }) {
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-field"
      style={{
        width: size,
        height: size,
        background: `rgb(var(--status-activity-${kind}-bg))`,
        color: `rgb(var(--status-activity-${kind}-fg))`,
      }}
    >
      <KindGlyph kind={kind} size={Math.round(size * 0.53)} />
    </span>
  )
}

function KindGlyph({ kind, size }: { kind: string; size: number }) {
  const Icon = KIND_ICONS[kind] ?? KIND_ICONS.note!
  return <Icon size={size} />
}

const KIND_ICONS: Record<string, (props: { size?: number }) => ReactNode> = {
  note: IconNote,
  task: IconTask,
  call: IconCall,
  email: IconEmail,
  sms: IconSms,
  meeting: IconMeeting,
}

// ---------------------------------------------------------------------------
// Peeks
// ---------------------------------------------------------------------------

export function JobsPeek({
  jobs, total, onOpen, onOpenAll,
}: {
  jobs: JobPeek[]
  total: number
  onOpen: (id: string) => void
  onOpenAll: () => void
}) {
  if (jobs.length === 0) return null

  return (
    <PeekCard title={`Jobs · ${jobs.length} open`} allLabel={`View all ${total}`} onOpenAll={onOpenAll}>
      {jobs.map((job) => (
        <button
          key={job.id}
          type="button"
          onClick={() => onOpen(job.id)}
          className="flex w-full items-center gap-3 border-t border-line-soft px-[18px] py-2.5 text-left"
        >
          {jobMark({
            id: job.id,
            status: job.status,
            serviceLine: job.serviceLine,
            divisionIcon: job.divisionIcon,
            divisionColour: job.divisionColour,
            jobType: job.jobType,
          })}
          <span className="min-w-0 flex-1">
            <b className="block truncate text-body font-semibold text-text">{job.title}</b>
            <span className="block truncate text-chip text-text-muted">
              {job.reference}
              {job.propertyName && ` · ${job.propertyName}`}
            </span>
          </span>
          <StatusChip group="job" status={job.status}>{humanize(job.status)}</StatusChip>
          {job.nextAction && (
            <span className={cn('hidden shrink-0 text-[12.5px] sm:block',
              job.nextActionUrgent ? 'font-semibold text-accent' : 'text-text-muted')}>
              {job.nextAction}
            </span>
          )}
          <span className="tnum w-[70px] shrink-0 text-right text-body font-bold text-text">
            {job.expected}
          </span>
        </button>
      ))}
    </PeekCard>
  )
}

export function VisitsPeek({
  visits, total, onOpen, onOpenAll,
}: {
  visits: VisitPeek[]
  total: number
  onOpen: (id: string) => void
  onOpenAll: () => void
}) {
  if (visits.length === 0) return null

  return (
    <PeekCard
      title="Visits · next 7 days"
      allLabel={`View all ${total}`}
      onOpenAll={onOpenAll}
    >
      {visits.map((visit) => (
        <button
          key={visit.id}
          type="button"
          onClick={() => onOpen(visit.id)}
          className="flex w-full items-center gap-3 border-t border-line-soft px-[18px] py-2.5 text-left"
        >
          {visitMark({
            id: visit.id, status: visit.status, scheduledDate: visit.scheduledDate,
          })}
          <span className="w-[90px] shrink-0 text-body font-semibold text-text">
            {new Date(`${visit.scheduledDate}T00:00:00Z`).toLocaleDateString('en-US', {
              weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
            })}
          </span>
          <span className="min-w-0 flex-1 truncate text-body text-text">{visit.description}</span>
          {visit.crewName && (
            <span className="hidden shrink-0 text-[12.5px] text-text-muted sm:block">
              {visit.crewName}
            </span>
          )}
          <StatusChip group="job" status={VISIT_STATUS_AS_JOB[visit.status] ?? 'scheduled'}>
            {humanize(visit.status)}
          </StatusChip>
        </button>
      ))}
    </PeekCard>
  )
}

export function NotesPeek({
  notes, onOpenAll,
}: {
  notes: RecordActivity[]
  onOpenAll: () => void
}) {
  if (notes.length === 0) return null

  return (
    <PeekCard title={`Notes · ${notes.length}`} allLabel="All notes" onOpenAll={onOpenAll}>
      {notes.map((note) => (
        <div key={note.id} className="flex gap-3 border-t border-line-soft px-[18px] py-3">
          <Avatar name={note.authorName ?? 'Someone'} size={28} />
          <div className="min-w-0 flex-1">
            {/*
              * A note is rich text — the composer writes `<b>` and highlights
              * — so it is rendered, not printed. `<NoteBody>` sanitises on the
              * way out, which is why this is the one component that draws it.
              */}
            <NoteBody html={note.body} className="leading-[1.45]" />
            <div className="mt-1 flex flex-wrap items-center gap-2 text-chip text-text-faint">
              <span className="rounded-tag bg-line-soft px-1.5 py-px text-[10.5px] font-semibold text-text-muted">
                {note.visibility === 'crew' ? 'Crew' : 'Internal'}
              </span>
              {note.pinnedAt && (
                <span
                  className="rounded-tag px-1.5 py-px text-[10.5px] font-semibold"
                  style={{
                    background: 'rgb(var(--status-activity-meeting-bg))',
                    color: 'rgb(var(--status-activity-meeting-fg))',
                  }}
                >
                  Pinned
                </span>
              )}
              {note.authorName} · {formatRelative(note.createdAt)}
            </div>
          </div>
        </div>
      ))}
    </PeekCard>
  )
}

function PeekCard({
  title, allLabel, onOpenAll, children,
}: {
  title: string
  allLabel: string
  onOpenAll: () => void
  children: ReactNode
}) {
  return (
    <RecordCard
      title={title}
      action={{
        label: allLabel,
        icon: <IconChevronRight size={13} />,
        iconAfter: true,
        onClick: onOpenAll,
      }}
    >
      {children}
    </RecordCard>
  )
}

/** What the Overview shows when the record is brand new. */
/**
 * What Overview draws while the summary is still out — §9, "Loading".
 *
 * **Neutral on purpose, and never either real layout.** The old code read
 * `empty = !summary || (…counts…)` and then guarded with `empty && summary`,
 * so a record that was still loading fell through to the *busy* branch and
 * drew Next up, Jobs, Visits and Notes with nothing in them for as long as
 * the request took. Nobody filed it, because empty cards on a record that is
 * about to have cards look like the page working.
 *
 * It stops looking like that the moment there is a quiet panel to flash
 * instead: a busy property would show six first steps and then replace them
 * with its real Overview. So loading gets its own answer, and both layouts
 * wait until the page knows which one it is.
 *
 * Three cards, a header bar and two rows of a square and two bars — the shape
 * of what is coming, carrying none of its words.
 */
export function OverviewSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-4">
      {[0, 1, 2].map((card) => (
        <section
          key={card}
          className="overflow-hidden rounded-record border border-line bg-surface"
        >
          <header className="border-b border-line-soft px-[18px] py-3">
            <SkeletonBar className="h-3.5 w-[140px]" />
          </header>
          <div className="flex flex-col gap-3.5 px-[18px] py-3.5">
            {[0, 1].map((row) => (
              <div key={row} className="flex items-center gap-3">
                <SkeletonBar className="h-9 w-9 rounded-tile" />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <SkeletonBar className="h-3 w-[45%]" />
                  <SkeletonBar className="h-2.5 w-[28%]" />
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

/**
 * One grey block.
 *
 * `motion-safe:animate-pulse` rather than a bare `animate-pulse`: §10 asks
 * for no pulse under `prefers-reduced-motion`, and Tailwind's variant is the
 * whole of that rule — a media query written by hand here would be a second
 * place to keep it.
 */
function SkeletonBar({ className }: { className?: string }) {
  return (
    <span
      className={cn('block rounded-full bg-line-soft motion-safe:animate-pulse', className)}
    />
  )
}

export function OverviewEmpty({ onAdd }: { onAdd: () => void }) {
  return (
    <EmptyState
      title="Nothing here yet"
      body="Notes, calls and tasks about this record will show up here."
      action={{ label: 'Add activity', onClick: onAdd }}
    />
  )
}
